import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Search, X, Loader2, Building2, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Permission constants
const WORK_ORDER_ASSIGN_ROLES = ['operations_manager', 'admin', 'super_admin'];

// Priority configuration
const PRIORITY_CONFIG = {
  low: { label: 'Low' },
  medium: { label: 'Medium' },
  high: { label: 'High' },
  urgent: { label: 'Urgent' },
};

// Category options
const CATEGORIES = [
  { value: 'repair', label: 'Repair' },
  { value: 'punch', label: 'Punch' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'service', label: 'Service' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'ops_task', label: 'Ops Task' },
];

/**
 * CreateWorkOrderModal - Reusable modal for creating work orders
 * 
 * @param {boolean} isOpen - Modal open state
 * @param {function} onClose - Callback when modal closes
 * @param {function} onSuccess - Callback when WO is created successfully (receives new WO)
 * @param {object} prefillData - Optional prefill data { job_id, job_name, customer_name, market_id }
 */
export const CreateWorkOrderModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  prefillData = {} 
}) => {
  const { user } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'ops_task',
    assigned_crew_id: '',
    assigned_user_id: '',
    due_date: '',
  });
  
  // Job selection state
  const [jobSearch, setJobSearch] = useState('');
  const [jobResults, setJobResults] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchingJobs, setSearchingJobs] = useState(false);
  
  // Reference data
  const [crews, setCrews] = useState([]);
  const [users, setUsers] = useState([]);
  
  // UI state
  const [saving, setSaving] = useState(false);
  
  // Permission checks
  const canAssign = user?.role && WORK_ORDER_ASSIGN_ROLES.includes(user.role.toLowerCase());
  
  // Initialize with prefill data when modal opens
  useEffect(() => {
    if (isOpen && prefillData.job_id) {
      setSelectedJob({
        id: prefillData.job_id,
        name: prefillData.job_name || 'Project',
        customer_name: prefillData.customer_name || '',
        address: prefillData.address || '',
        market_id: prefillData.market_id || ''
      });
    }
  }, [isOpen, prefillData]);
  
  // Fetch crews and users for assignment
  useEffect(() => {
    const fetchReferenceData = async () => {
      if (!isOpen) return;
      
      try {
        const [crewsRes, usersRes] = await Promise.all([
          axios.get(`${API}/ops/crews`).catch(() => ({ data: { crews: [] } })),
          canAssign ? axios.get(`${API}/users`) : Promise.resolve({ data: [] })
        ]);
        setCrews(crewsRes.data.crews || []);
        setUsers(usersRes.data || []);
      } catch (err) {
        console.error('Error fetching reference data:', err);
      }
    };
    fetchReferenceData();
  }, [isOpen, canAssign]);
  
  // Job search
  useEffect(() => {
    const searchJobs = async () => {
      if (jobSearch.length < 2 || prefillData.job_id) {
        setJobResults([]);
        return;
      }
      setSearchingJobs(true);
      try {
        const res = await axios.get(`${API}/ops/jobs/search?q=${encodeURIComponent(jobSearch)}&limit=10`);
        setJobResults(res.data.jobs || []);
      } catch (err) {
        console.error('Error searching jobs:', err);
      } finally {
        setSearchingJobs(false);
      }
    };
    
    const debounce = setTimeout(searchJobs, 300);
    return () => clearTimeout(debounce);
  }, [jobSearch, prefillData.job_id]);
  
  // Reset form when modal closes
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      category: 'ops_task',
      assigned_crew_id: '',
      assigned_user_id: '',
      due_date: '',
    });
    setJobSearch('');
    setJobResults([]);
    setSelectedJob(null);
  };
  
  const handleClose = () => {
    resetForm();
    onClose();
  };
  
  const handleCreate = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!selectedJob) {
      toast.error('Please select a project');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        priority: formData.priority,
        category: formData.category,
        job_id: selectedJob.id,
        assigned_crew_id: formData.assigned_crew_id || null,
        assigned_user_id: formData.assigned_user_id || null,
        due_date: formData.due_date || null,
      };
      
      const res = await axios.post(`${API}/ops/work-orders`, payload);
      toast.success(`Work order ${res.data.work_order_number} created`);
      
      if (onSuccess) {
        onSuccess(res.data);
      }
      
      handleClose();
    } catch (err) {
      console.error('Error creating work order:', err);
      toast.error(err.response?.data?.detail || 'Failed to create work order');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-white" data-testid="create-wo-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">Create Work Order</DialogTitle>
          <DialogDescription className="text-slate-400">
            Create a new work order for service, repair, or follow-up work
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Project Selection */}
          <div className="space-y-2">
            <Label className="text-slate-300">Project *</Label>
            {selectedJob ? (
              <div className="flex items-center justify-between p-3 bg-slate-800 border border-slate-600 rounded-lg">
                <div>
                  <div className="font-medium text-white">{selectedJob.name || selectedJob.address || 'Selected Project'}</div>
                  <div className="text-sm text-slate-400 flex items-center gap-2">
                    {selectedJob.customer_name && <span>{selectedJob.customer_name}</span>}
                    {selectedJob.address && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {selectedJob.address}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                {!prefillData.job_id && (
                  <Button variant="ghost" size="sm" onClick={() => setSelectedJob(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search projects by name, customer, or address..."
                  value={jobSearch}
                  onChange={(e) => setJobSearch(e.target.value)}
                  className="pl-9 bg-slate-700 border-slate-600"
                  data-testid="modal-job-search-input"
                />
                {searchingJobs && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
                )}
                {jobResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                    {jobResults.map(job => (
                      <div
                        key={job.id}
                        onClick={() => {
                          setSelectedJob(job);
                          setJobSearch('');
                          setJobResults([]);
                        }}
                        className="p-3 hover:bg-slate-700/50 cursor-pointer border-b border-slate-700/50 last:border-0"
                      >
                        <div className="font-medium text-white">{job.name || 'Unnamed Project'}</div>
                        <div className="text-sm text-slate-400">{job.customer_name} • {job.address}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label className="text-slate-300">Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g., Repair damaged soffit panels"
              className="bg-slate-700 border-slate-600"
              data-testid="modal-wo-title-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-slate-300">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe the work to be done..."
              className="bg-slate-700 border-slate-600 min-h-[80px]"
            />
          </div>

          {/* Priority & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Priority</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData(f => ({ ...f, priority: v }))}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {Object.entries(PRIORITY_CONFIG).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData(f => ({ ...f, category: v }))}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Assignment */}
          {canAssign && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Assign to Crew</Label>
                <Select 
                  value={formData.assigned_crew_id || 'none'} 
                  onValueChange={(v) => setFormData(f => ({ ...f, assigned_crew_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue placeholder="Select crew" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="none">No crew</SelectItem>
                    {crews.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Assign to User</Label>
                <Select 
                  value={formData.assigned_user_id || 'none'} 
                  onValueChange={(v) => setFormData(f => ({ ...f, assigned_user_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="none">No user</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Due Date */}
          <div className="space-y-2">
            <Label className="text-slate-300">Due Date</Label>
            <Input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData(f => ({ ...f, due_date: e.target.value }))}
              className="bg-slate-700 border-slate-600"
            />
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose} 
            className="border-slate-600" 
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreate} 
            className="bg-blue-600 hover:bg-blue-500" 
            disabled={saving}
            data-testid="modal-create-wo-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Work Order'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateWorkOrderModal;
